import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { archiveByKey, archiveLayerMap, fetchArchives, type Archive } from '../api/archives';
import { ApiError, backend } from '../api/backend';
import {
  getItemText,
  getLayers,
  stripHtml,
  type PandoraAnnotation,
  type PandoraItemText,
  type PandoraLayers,
} from '../api/pandora';
import { activePandoraSourcesAt, cueAt } from '../engine/activeSources';
import {
  downloadBlob,
  ExportCancelledError,
  exportRealtime,
  exportWithMediabunny,
  webCodecsAvailable,
  type CancelToken,
} from '../engine/exporter';
import { getFrameCache, pickFrameResolution } from '../engine/framecache';
import { concatTime, getPartOffsets } from '../engine/partoffsets';
import { PreviewEngine } from '../engine/preview';
import { formatTime, totalDuration } from '../format/helpers';
import type { Edit } from '../format/types';
import Transport from './Transport';
import { useBufferingIndicator } from './useBufferingIndicator';
import './PublicViewerPage.css';

type Phase = 'loading' | 'ready' | 'notfound' | 'error';

export default function PublicViewerPage() {
  const { token } = useParams<{ token: string }>();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const editRef = useRef<Edit | null>(null);
  const engineRef = useRef<PreviewEngine | null>(null);

  const [phase, setPhase] = useState<Phase>('loading');
  const [meta, setMeta] = useState<{ title: string; description: string }>({
    title: '',
    description: '',
  });
  const [archives, setArchives] = useState<Archive[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [bufferingRaw, setBuffering] = useState(false);
  const buffering = useBufferingIndicator(bufferingRaw);
  const [duration, setDuration] = useState(0);

  // ---- load edit + archives, build the engine -----------------------------
  useEffect(() => {
    if (!token) {
      setPhase('notfound');
      return;
    }
    let disposed = false;
    void (async () => {
      try {
        const [arcs, pub] = await Promise.all([fetchArchives(), backend.public.get(token)]);
        if (disposed) return;
        editRef.current = pub.timeline;
        setArchives(arcs);
        setMeta({ title: pub.title, description: pub.description });
        setDuration(totalDuration(pub.timeline));
        document.title = pub.title ? `${pub.title} — fabled` : 'fabled';
        setPhase('ready');
      } catch (e) {
        if (disposed) return;
        if (e instanceof ApiError && e.status === 404) setPhase('notfound');
        else setPhase('error');
      }
    })();
    return () => {
      disposed = true;
    };
  }, [token]);

  // Build the engine once the canvas is mounted and the edit is ready.
  useEffect(() => {
    if (phase !== 'ready') return;
    const canvas = canvasRef.current;
    const edit = editRef.current;
    if (!canvas || !edit) return;

    // Choose a stream resolution matched to the on-screen canvas height.
    const displayH = Math.round(
      (canvas.clientHeight || edit.settings.height) * (window.devicePixelRatio || 1),
    );
    const engine = new PreviewEngine(canvas, () => editRef.current as Edit, archives, {
      resolution: displayH,
    });
    engine.onTime = (t) => setCurrentTime(t);
    engine.onEnded = () => setPlaying(false);
    engine.onBuffering = (ids) => setBuffering(ids.length > 0);
    engineRef.current = engine;
    engine.seek(0);
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
    // archives/edit are stable once ready; run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ---- preconnect to distinct archive origins referenced by the edit ------
  useEffect(() => {
    if (phase !== 'ready') return;
    const edit = editRef.current;
    if (!edit) return;
    const sites = new Set<string>();
    for (const src of Object.values(edit.sources)) {
      if (src.kind !== 'pandora-video') continue;
      const archive = archiveByKey(archives, src.archive);
      if (archive) sites.add(archive.site);
    }
    const links: HTMLLinkElement[] = [];
    for (const site of sites) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = site;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      links.push(link);
    }
    return () => {
      for (const l of links) l.remove();
    };
  }, [phase, archives]);

  // ---- frame-cache warming: prefetch upcoming clips' in-point frames ------
  const warmedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (phase !== 'ready') return;
    const edit = editRef.current;
    if (!edit) return;
    const LOOKAHEAD = 4;
    for (const track of edit.tracks) {
      if (track.kind !== 'video') continue;
      for (const clip of track.clips) {
        if (clip.start <= currentTime || clip.start - currentTime > LOOKAHEAD) continue;
        const src = edit.sources[clip.source];
        if (!src || src.kind !== 'pandora-video') continue;
        const archive = archiveByKey(archives, src.archive);
        if (!archive) continue;
        const key = `${src.archive}:${src.item}:${clip.in}`;
        if (warmedRef.current.has(key)) continue;
        warmedRef.current.add(key);
        const res = pickFrameResolution(archive.resolutions);
        void getPartOffsets(archive.site, src.item)
          .then((offsets) => concatTime(offsets, src.part, clip.in))
          .catch(() => clip.in)
          .then((cT) => getFrameCache().getFrame(archive, src.item, cT, { resolution: res }));
      }
    }
  }, [phase, currentTime, archives]);

  // ---- transport ----------------------------------------------------------
  const toggle = useCallback(() => {
    const e = engineRef.current;
    if (!e) return;
    if (e.isPlaying) {
      e.pause();
      setPlaying(false);
    } else {
      e.play();
      setPlaying(true);
    }
  }, []);

  const seek = useCallback((t: number) => {
    const e = engineRef.current;
    if (!e) return;
    e.seek(t);
    setCurrentTime(t);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
  }, []);

  if (phase === 'loading') {
    return <div className="viewer-status">Loading…</div>;
  }
  if (phase === 'notfound') {
    return (
      <div className="viewer-status">
        <h1>This video isn’t available</h1>
        <p>The link may be wrong, or the video has been unpublished.</p>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div className="viewer-status">
        <h1>Something went wrong</h1>
        <p>Couldn’t load this video. Please try again later.</p>
      </div>
    );
  }

  const edit = editRef.current as Edit;

  return (
    <div className="viewer">
      <div className="viewer-main">
        <div className="viewer-stage" ref={stageRef}>
          <canvas
            ref={canvasRef}
            className="viewer-canvas"
            style={{ aspectRatio: `${edit.settings.width} / ${edit.settings.height}` }}
            onClick={toggle}
          />
          {buffering && (
            <div className="viewer-buffering" aria-live="polite">
              <span className="viewer-buffering-dot" /> loading video…
            </div>
          )}
        </div>

        <Transport
          playing={playing}
          currentTime={currentTime}
          duration={duration}
          onToggle={toggle}
          onSeek={seek}
          scrubber
          className="viewer-transport"
          playClassName="viewer-play"
          timecodeClassName="viewer-timecode"
        >
          <button className="icon" title="Fullscreen" onClick={toggleFullscreen}>
            ⛶
          </button>
        </Transport>

        <div className="viewer-meta">
          <h1 className="viewer-title">{meta.title || 'Untitled'}</h1>
          {meta.description && <p className="viewer-desc">{meta.description}</p>}
          <DownloadButton
            edit={edit}
            archives={archives}
            engineRef={engineRef}
            title={meta.title}
          />
        </div>
      </div>

      <aside className="viewer-side">
        <AnnotationsPanel edit={edit} archives={archives} currentTime={currentTime} />
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Download (client-side WebCodecs export, encodes in the visitor's browser)
// ---------------------------------------------------------------------------

function DownloadButton({
  edit,
  archives,
  engineRef,
  title,
}: {
  edit: Edit;
  archives: Archive[];
  engineRef: React.MutableRefObject<PreviewEngine | null>;
  /** Published project name, so the file is not called "export.mp4". */
  title: string;
}) {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<CancelToken>({ cancelled: false });

  async function download() {
    setBusy(true);
    setError(null);
    setPct(0);
    cancelRef.current = { cancelled: false };
    const onProgress = (p: { framesDone: number; framesTotal: number }) =>
      setPct(Math.round((p.framesDone / Math.max(1, p.framesTotal)) * 100));
    try {
      let result;
      if (webCodecsAvailable()) {
        try {
          result = await exportWithMediabunny(
            edit,
            archives,
            {
              container: 'mp4',
              width: edit.settings.width,
              height: edit.settings.height,
              title,
              onProgress,
            },
            cancelRef.current,
          );
        } catch (e) {
          if (e instanceof ExportCancelledError) throw e;
          const engine = engineRef.current;
          if (!engine) throw e;
          result = await exportRealtime(engine, edit, { title, onProgress }, cancelRef.current);
        }
      } else {
        const engine = engineRef.current;
        if (!engine) throw new Error('preview unavailable');
        result = await exportRealtime(engine, edit, { title, onProgress }, cancelRef.current);
      }
      downloadBlob(result.blob, result.filename);
    } catch (e) {
      if (!(e instanceof ExportCancelledError))
        setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setBusy(false);
      setPct(0);
    }
  }

  if (busy) {
    return (
      <div className="viewer-download-busy">
        <span>Encoding in your browser… {pct}%</span>
        <button className="ghost" onClick={() => (cancelRef.current.cancelled = true)}>
          Cancel
        </button>
      </div>
    );
  }
  return (
    <>
      <button className="viewer-download" onClick={() => void download()}>
        ⬇ Download video
      </button>
      {error && <p className="viewer-download-error">{error}</p>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Contextual pandora annotations for the clip currently on screen
// ---------------------------------------------------------------------------

interface ItemData {
  layers: PandoraLayers;
  text: PandoraItemText;
  offsets: number[];
}

function AnnotationsPanel({
  edit,
  archives,
  currentTime,
}: {
  edit: Edit;
  archives: Archive[];
  currentTime: number;
}) {
  // Per-item cache of fetched pandora data, keyed by "archiveKey/item".
  const cacheRef = useRef<Map<string, ItemData | 'loading'>>(new Map());
  const [, force] = useState(0);

  const active = activePandoraSourcesAt(edit, currentTime);
  const src = active[0]; // typically a single active source

  // Fetch (once) the pandora data for the active item.
  useEffect(() => {
    if (!src) return;
    const key = `${src.archiveKey}/${src.item}`;
    if (cacheRef.current.has(key)) return;
    const archive = archiveByKey(archives, src.archiveKey);
    if (!archive) return;
    cacheRef.current.set(key, 'loading');
    void Promise.all([
      getLayers(archive.site, src.item).catch(() => ({}) as PandoraLayers),
      getItemText(archive.site, src.item).catch(
        () => ({ summary: '', keywords: [] }) as PandoraItemText,
      ),
      getPartOffsets(archive.site, src.item).catch(() => [] as number[]),
    ]).then(([layers, text, offsets]) => {
      cacheRef.current.set(key, { layers, text, offsets });
      force((n) => n + 1);
    });
  }, [src?.archiveKey, src?.item, archives, src]);

  if (!src) {
    return <div className="anno anno-empty">No source annotations here.</div>;
  }
  const archive = archiveByKey(archives, src.archiveKey);
  const key = `${src.archiveKey}/${src.item}`;
  const data = cacheRef.current.get(key);
  if (!archive || !data || data === 'loading') {
    return <div className="anno anno-empty">Loading annotations…</div>;
  }

  const cT = concatTime(data.offsets, src.part, src.mediaTime);
  const layerMap = archiveLayerMap(src.archiveKey);

  // Current subtitle/transcript cue across the item's subtitle layers.
  const subtitleCues: PandoraAnnotation[] = [];
  for (const layerId of layerMap.subtitleLayers) {
    const anns = data.layers[layerId];
    if (anns) subtitleCues.push(...anns);
  }
  subtitleCues.sort((a, b) => a.in - b.in);
  const cue = cueAt(subtitleCues, cT);

  const description = stripHtml(data.text.summary);
  const keywords = data.text.keywords;

  // Link-back to the exact on-screen segment of the source.
  const cIn = concatTime(data.offsets, src.part, src.clip.in);
  const cOut = concatTime(data.offsets, src.part, src.clip.out);
  const linkHref = `${archive.site}/${src.item}/${cIn.toFixed(3)}-${cOut.toFixed(3)}`;

  return (
    <div className="anno">
      {cue && <div className="anno-cue">{stripHtml(cue.value)}</div>}

      <a className="anno-linkback" href={linkHref} target="_blank" rel="noopener">
        View on {archive.name} · {formatTime(cT)}
      </a>

      {keywords.length > 0 && (
        <div className="anno-keywords">
          {keywords.map((k) => (
            <span className="anno-chip" key={k}>
              {k}
            </span>
          ))}
        </div>
      )}

      {description && (
        <details className="anno-desc" open>
          <summary>About this source</summary>
          <p>{description}</p>
        </details>
      )}
    </div>
  );
}
